/**
 * @module engine3d/pipes3
 * @summary Pipework that exists only in the 3D view — drawn by hand, joined to
 *   nozzles or to other pipes, and deleted without touching the 2D drawing.
 * @description
 *   The 2D document is a schematic: it records what is connected to what. Real
 *   pipework also has to record *how* — up this wall, along that rack, down to
 *   the nozzle — and pushing those runs back into the schematic would wreck it.
 *   So they live in their own list (`S.pipes3`), which the 2D canvas never
 *   reads.
 *
 *   Two rules make the result look built rather than sketched:
 *
 *     • **Every leg is axis-aligned.** A vertex is snapped onto one axis from
 *       its predecessor, so a run is only ever horizontal or vertical. That is
 *       what makes the elbows come out horizontal or vertical too — a bend
 *       between two horizontal legs lies flat, one between a horizontal and a
 *       vertical leg stands up — with no orientation for anyone to set by hand.
 *     • **Bend radius is a property of the run.** Long, short or mitred, chosen
 *       per pipe, exactly as it would be specified on a real line.
 */
import type { BendStyle, LegNote, Pipe3, Pipe3Node, PortKey, Shape } from '../types';
import { S } from '../engine/state';
import { v3, vAdd, vMul, vNorm, vSub, type Vec3 } from './math3';
import { cylinder, densify, filletPath, frustum, merge, rotateX, rotateY, translate, tube, type Mesh } from './mesh';
import { portWorld } from './pipes';

/** Default outside radius of a 3D-drawn process pipe. */
export const DEFAULT_R = 3.2;
/** How far a run leaves a nozzle before it is allowed to turn. */
const STUB = 10;

/** Bend radius as a multiple of the pipe radius. */
const BEND_FACTOR: Record<BendStyle, number> = {
  long: 3.4,
  short: 1.7,
  // Not zero: `filletPath` needs something to work with, and a hair of radius
  // is what stops the sweep pinching itself inside out at the corner.
  mitre: 0.18,
};

export const radiusOf = (q: Pipe3): number => q.r ?? (q.service ? 1.7 : DEFAULT_R);
export const bendRadiusOf = (q: Pipe3): number => radiusOf(q) * BEND_FACTOR[q.bend ?? 'long'];

// ─── Node resolution ─────────────────────────────────────────────────────

/** A resolved pipe end: where it is, and which way the pipe leaves it. */
export interface EndPoint {
  p: Vec3;
  /** Outward direction, or null for a free end that imposes nothing. */
  n: Vec3 | null;
}

/** Points along a pipe's centre-line, used for tee attachment and picking. */
export function centreLine(q: Pipe3): Vec3[] {
  const pts = q.pts.map((v) => v3(v.x, v.y, v.z));
  const a = resolveNode(q.from, q.id);
  const b = resolveNode(q.to, q.id);
  const out: Vec3[] = [];
  if (a) {
    out.push(a.p);
    if (a.n) out.push(vAdd(a.p, vMul(a.n, STUB)));
  }
  out.push(...pts);
  if (b) {
    if (b.n) out.push(vAdd(b.p, vMul(b.n, STUB)));
    out.push(b.p);
  }
  return dedupe(out);
}

/** The point a fraction `t` along a pipe, plus the direction there. */
export function pointAlong(q: Pipe3, t: number): EndPoint | null {
  const line = centreLine(q);
  if (line.length < 2) return null;
  const lens: number[] = [];
  let total = 0;
  for (let i = 1; i < line.length; i++) {
    const d = Math.hypot(line[i].x - line[i - 1].x, line[i].y - line[i - 1].y, line[i].z - line[i - 1].z);
    lens.push(d);
    total += d;
  }
  if (total < 1e-6) return { p: line[0], n: null };
  let want = Math.max(0, Math.min(1, t)) * total;
  for (let i = 0; i < lens.length; i++) {
    if (want <= lens[i] || i === lens.length - 1) {
      const f = lens[i] > 1e-6 ? want / lens[i] : 0;
      const dir = vNorm(vSub(line[i + 1], line[i]));
      return { p: vAdd(line[i], vMul(dir, lens[i] * f)), n: dir };
    }
    want -= lens[i];
  }
  return { p: line[line.length - 1], n: null };
}

/**
 * Resolve one end of a pipe to a world point.
 * `guard` is the id of the pipe being resolved, so a tee that somehow points at
 * its own run cannot recurse for ever.
 */
export function resolveNode(node: Pipe3Node, guard?: string): EndPoint | null {
  if (node.shapeId) {
    const s = S.shapes.find((x) => x.id === node.shapeId);
    if (!s) return null;
    const wp = portWorld(s, (node.port ?? 'E') as PortKey);
    return { p: wp.p, n: wp.n };
  }
  if (node.pipeId && node.pipeId !== guard) {
    const target = S.pipes3.find((x) => x.id === node.pipeId);
    if (!target) return null;
    const hit = pointAlong(target, node.t ?? 0.5);
    // A tee leaves its parent at right angles; the branch's own first leg
    // decides which way, so impose nothing here.
    return hit ? { p: hit.p, n: null } : null;
  }
  if (node.p) return { p: v3(node.p.x, node.p.y, node.p.z), n: null };
  return null;
}

function dedupe(pts: Vec3[]): Vec3[] {
  const out: Vec3[] = [];
  for (const q of pts) {
    const last = out[out.length - 1];
    if (last && Math.hypot(q.x - last.x, q.y - last.y, q.z - last.z) < 0.4) continue;
    out.push(q);
  }
  return out;
}

// ─── Axis locking ────────────────────────────────────────────────────────

export type AxisMode = 'auto' | 'horizontal' | 'vertical' | 'diagonal';

/**
 * Pull `to` onto a single axis from `from`.
 *
 * This is what guarantees the run is only ever horizontal or vertical, and
 * therefore what makes every elbow come out square. `auto` keeps whichever axis
 * the pointer travelled furthest along, which is what a person means when they
 * drag roughly sideways or roughly up.
 */
export function lockAxis(from: Vec3, to: Vec3, mode: AxisMode): Vec3 {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  // A diagonal leg is the one case with nothing to lock.
  if (mode === 'diagonal') return to;
  if (mode === 'vertical') return v3(from.x, to.y, from.z);
  if (mode === 'horizontal')
    return Math.abs(dx) >= Math.abs(dz) ? v3(to.x, from.y, from.z) : v3(from.x, from.y, to.z);
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  const az = Math.abs(dz);
  if (ay >= ax && ay >= az) return v3(from.x, to.y, from.z);
  return ax >= az ? v3(to.x, from.y, from.z) : v3(from.x, from.y, to.z);
}

// ─── Geometry ────────────────────────────────────────────────────────────

export interface Pipe3Geometry {
  body: Mesh;
  fittings: Mesh;
  line: Vec3[];
  radius: number;
  /**
   * The FILLETED centre-line the body was swept along — `line` with its elbows
   * rounded. Distance along the run, which is what the flow animation is a
   * function of, is measured on this rather than on the square-cornered `line`.
   */
  centre: Vec3[];
  /** Distance along the run at each vertex of `body`, in world units. */
  arc: number[];
}

/** Aim a mesh's +Y axis down `dir`, then move it to `at`. */
function orient(m: Mesh, at: Vec3, dir: Vec3): Mesh {
  const l = Math.hypot(dir.x, dir.y, dir.z) || 1;
  rotateX(m, Math.acos(Math.max(-1, Math.min(1, dir.y / l))));
  rotateY(m, Math.atan2(dir.x, dir.z));
  return translate(m, at.x, at.y, at.z);
}

/**
 * Build the geometry for one 3D-only pipe, or null if an end is unresolvable.
 *
 * @param flowStep  Longest allowed section of pipe wall, or 0 for the default
 *   sweep. See the note on `buildPipe` — only the flow animation wants it, and
 *   only while it is running.
 */
export function buildPipe3(q: Pipe3, flowStep = 0): Pipe3Geometry | null {
  const line = centreLine(q);
  if (line.length < 2) return null;
  const r = radiusOf(q);
  const seg = q.service ? 8 : 12;
  const centre = filletPath(line, bendRadiusOf(q), q.bend === 'mitre' ? 2 : 5);
  const arc: number[] = [];
  const body = tube(densify(centre, flowStep), r, seg, true, arc);

  const fittings: Mesh[] = [];
  // A flange wherever the run lands on a nozzle. A free end gets a blank cap,
  // and a tee gets neither — it is welded into its parent.
  for (const [node, at, dir] of [
    [q.from, line[0], vNorm(vSub(line[1], line[0]))],
    [q.to, line[line.length - 1], vNorm(vSub(line[line.length - 2], line[line.length - 1]))],
  ] as [Pipe3Node, Vec3, Vec3][]) {
    if (node.shapeId) fittings.push(orient(cylinder(r * 1.8, 1.8, { seg }), vAdd(at, vMul(dir, 0.4)), dir));
    else if (node.p) fittings.push(orient(frustum(r * 1.25, r * 1.25, 2.2, { seg }), at, dir));
  }
  return { body, fittings: merge(...fittings), line, radius: r, centre, arc };
}

// ─── Continuing a run ────────────────────────────────────────────────────

/**
 * One end of an existing run, named so a new run can be joined onto it.
 */
export interface EndRef {
  pipeId: string;
  which: 'from' | 'to';
}

/**
 * A run flattened for joining: its two end nodes with the vertices between.
 * Pipes are stored this way already; the type exists so a run being DRAWN can
 * be handled by exactly the same code as one already on the drawing.
 */
export interface Chain {
  from: Pipe3Node;
  pts: { x: number; y: number; z: number }[];
  to: Pipe3Node;
}

export const chainOf = (q: Pipe3): Chain => ({
  from: q.from,
  pts: q.pts.map((v) => ({ ...v })),
  to: q.to,
});

/** The same run walked the other way, so either end can be treated as the tail. */
export const reverseChain = (c: Chain): Chain => ({
  from: c.to,
  pts: c.pts.slice().reverse(),
  to: c.from,
});

/**
 * An end that a new run may continue from: one pinned to nothing but a point.
 *
 * An end bolted to a nozzle is already terminated — there is nothing past a
 * flange to continue into — and one teed into another run belongs to that run.
 * Only a bare point is a loose end.
 */
export function freeEnd(q: Pipe3, which: 'from' | 'to'): Vec3 | null {
  const node = which === 'from' ? q.from : q.to;
  if (node.shapeId || node.pipeId || !node.p) return null;
  return v3(node.p.x, node.p.y, node.p.z);
}

/**
 * Butt two runs together at the point they share.
 *
 * The shared point becomes an INTERIOR vertex of the result, and that is the
 * whole trick: `filletPath` rounds every interior vertex, so the joint comes
 * out as a real elbow instead of two pipes ending against each other.
 */
export function joinChains(a: Chain, b: Chain): Chain {
  const joint = a.to.p ?? b.from.p;
  const pts = a.pts.map((v) => ({ ...v }));
  if (joint) pts.push({ ...joint });
  pts.push(...b.pts.map((v) => ({ ...v })));
  return { from: a.from, pts, to: b.to };
}

/** Centre-line length of a run, stubs included — the measure `t` is a fraction of. */
export function runLength(q: Pipe3): number {
  const line = centreLine(q);
  let total = 0;
  for (let i = 1; i < line.length; i++)
    total += Math.hypot(line[i].x - line[i - 1].x, line[i].y - line[i - 1].y, line[i].z - line[i - 1].z);
  return total;
}

/** Every pipe-anchored node in the document: branches off runs, and dimension ends. */
function anchoredNodes(): Pipe3Node[] {
  const out: Pipe3Node[] = [];
  for (const q of S.pipes3) out.push(q.from, q.to);
  for (const d of S.dims3) out.push(d.from, d.to);
  return out;
}

/**
 * Move everything anchored to `fromId` onto `toId`, rewriting how far along it
 * sits.
 *
 * `t` is a fraction of the whole run, so making a run longer slides every
 * branch on it towards the start. Because a run is only ever continued from a
 * FREE end, the old centre-line survives whole inside the new one — as a
 * prefix, a suffix, or reversed — and the fractions can be mapped exactly
 * instead of being left to drift.
 */
export function retargetAnchors(fromId: string, toId: string, map: (t: number) => number): void {
  for (const node of anchoredNodes()) {
    if (node.pipeId !== fromId) continue;
    node.pipeId = toId;
    const t = map(node.t ?? 0.5);
    node.t = t < 0 ? 0 : t > 1 ? 1 : t;
  }
}

/** What a freshly drawn run is made of, when it is not inheriting from another. */
export interface RunDefaults {
  bend: BendStyle;
  service: boolean;
}

/**
 * Lay a drawn chain down as pipework, continuing and absorbing what it touches.
 *
 * Three cases, and they compose:
 *   • **Continuing.** `join.start` names the loose end the run was drawn from.
 *     That run grows; no second pipe appears; the old end point becomes a bend.
 *   • **Landing.** `join.end` names the loose end it was drawn onto. That run
 *     is absorbed into the result, so what looked like two runs meeting is one
 *     run with a bend in it.
 *   • **Neither.** A new run, exactly as before.
 *
 * The surviving run keeps the OLDER pipe's id and its bend, colour and size —
 * so the selection, the branches on it and everything the engineer already set
 * come through the join unchanged.
 *
 * @returns the id of the run that now carries the drawn chain.
 */
export function commitRun(
  chain: Chain,
  join: { start?: EndRef; end?: EndRef },
  def: RunDefaults,
): string {
  const A = join.start ? findPipe3(join.start.pipeId) : undefined;
  let B = join.end ? findPipe3(join.end.pipeId) : undefined;
  // A run has one start and one end, so it cannot be closed into a loop. Left
  // as it is, the landing stays an ordinary tee.
  if (B && A && B.id === A.id) B = undefined;

  const lenA = A ? runLength(A) : 0;
  const lenB = B ? runLength(B) : 0;
  const revA = !!A && join.start!.which === 'from';
  const revB = !!B && join.end!.which === 'to';

  let out = chain;
  // Orient the run being continued so the loose end is its TAIL, then the drawn
  // chain simply follows it.
  if (A) out = joinChains(revA ? reverseChain(chainOf(A)) : chainOf(A), out);
  // Orient the run being landed on so the loose end is its HEAD, and it follows
  // the drawn chain in turn.
  if (B) out = joinChains(out, revB ? reverseChain(chainOf(B)) : chainOf(B));

  // Carrying on from a run's START had to walk that run backwards to get there,
  // which would leave the finished pipe pointing the other way. The drawing is
  // a flow diagram: which end is `from` is not decoration. Turning the whole
  // result around puts the surviving run back the way it was drawn, and the
  // new stretch simply arrives at its head instead of its tail.
  const flip = A ? revA : revB;
  if (flip) out = reverseChain(out);
  // Where each original run ended up in the result, and which way round it is:
  // after the turn, the piece that was at the head is at the tail, and each
  // reversal cancels the one applied above.
  const headA = !flip;
  const revdA = flip ? !revA : revA;
  const headB = flip;
  const revdB = flip ? !revB : revB;

  const survivor: Pipe3 = A ??
    B ?? {
      id: nextPipeId(),
      from: out.from,
      to: out.to,
      pts: [],
      bend: def.bend,
      service: def.service || undefined,
    };
  survivor.from = out.from;
  survivor.to = out.to;
  survivor.pts = out.pts;
  if (!A && !B) S.pipes3.push(survivor);

  // Only when BOTH runs are in play is one of them absorbed. Landing on a
  // loose end with nothing behind you grows that run instead — it is the
  // survivor, and it stays where it is.
  const absorbed = A && B ? B : undefined;
  if (absorbed) {
    // Spliced out rather than deleted: `deletePipe3` would freeze its branches
    // into loose points, and they are about to be re-anchored properly.
    const i = S.pipes3.findIndex((q) => q.id === absorbed.id);
    if (i >= 0) S.pipes3.splice(i, 1);
  }

  const newLen = runLength(survivor);
  if (newLen > 1e-6) {
    /**
     * Where a fraction of one of the original runs now sits in the result.
     * The old centre-line survives WHOLE inside the new one — a run is only
     * ever continued from a loose end, so nothing was cut out of the middle —
     * which is what makes an exact map possible instead of a guess.
     */
    const mapInto = (atHead: boolean, reversed: boolean, len: number) => (t: number) => {
      const local = reversed ? 1 - t : t;
      return ((atHead ? 0 : newLen - len) + local * len) / newLen;
    };
    if (A) retargetAnchors(A.id, A.id, mapInto(headA, revdA, lenA));
    if (B) retargetAnchors(B.id, survivor.id, mapInto(headB, revdB, lenB));
    // Captions and hidden figures travel with the pipe they were put on, by the
    // very same map. This is what lets a run be continued — which reverses it
    // when you carry on from its start — without its notes sliding onto other
    // legs.
    if (A) retargetLegNotes(A, mapInto(headA, revdA, lenA));
    if (B) {
      const m = mapInto(headB, revdB, lenB);
      if (B === survivor) retargetLegNotes(B, m);
      else mergeLegNotes(survivor, B, m);
    }
  }
  return survivor.id;
}

// ─── Editing ─────────────────────────────────────────────────────────────

let counter = 0;

/** Fresh id. Uses a counter, not a clock, so headless runs stay deterministic. */
export function nextPipeId(): string {
  counter++;
  let id = 'p3_' + counter;
  while (S.pipes3.some((q) => q.id === id)) {
    counter++;
    id = 'p3_' + counter;
  }
  return id;
}

/** Keep the id counter clear of whatever a loaded document already uses. */
export function syncPipeCounter(): void {
  for (const q of S.pipes3) {
    const m = /^p3_(\d+)$/.exec(q.id);
    if (m) counter = Math.max(counter, parseInt(m[1], 10));
  }
}

export function findPipe3(id: string): Pipe3 | undefined {
  return S.pipes3.find((q) => q.id === id);
}

/** True when this id belongs to a 3D-only pipe rather than a 2D connector. */
export function isPipe3Id(id: string): boolean {
  return S.pipes3.some((q) => q.id === id);
}

/** Remove a 3D-only pipe, and cut loose anything teed into it. */
export function deletePipe3(id: string): boolean {
  const i = S.pipes3.findIndex((q) => q.id === id);
  if (i < 0) return false;
  const doomed = S.pipes3[i];
  // A branch off the deleted run would resolve to nothing; freeze it in place
  // as a free end so it stays visible and editable instead of vanishing.
  for (const q of S.pipes3) {
    for (const node of [q.from, q.to]) {
      if (node.pipeId !== id) continue;
      const at = pointAlong(doomed, node.t ?? 0.5);
      delete node.pipeId;
      delete node.t;
      node.p = at ? { x: at.p.x, y: at.p.y, z: at.p.z } : { x: 0, y: 0, z: 0 };
    }
  }
  S.pipes3.splice(i, 1);
  return true;
}

/** Shift one vertex of a 3D pipe. Index -1 addresses a free `from` end. */
export function movePipe3Point(q: Pipe3, index: number, to: Vec3): void {
  const r1 = (v: number) => Math.round(v * 10) / 10;
  if (index === -1) {
    if (q.from.p) q.from.p = { x: r1(to.x), y: r1(to.y), z: r1(to.z) };
    return;
  }
  if (index === q.pts.length) {
    if (q.to.p) q.to.p = { x: r1(to.x), y: r1(to.y), z: r1(to.z) };
    return;
  }
  if (index < 0 || index >= q.pts.length) return;
  q.pts[index] = { x: r1(to.x), y: r1(to.y), z: r1(to.z) };
}

/**
 * The logical vertices of a run: its start, each bend, its end.
 *
 * These are the points a person drew, as opposed to the centre-line, which also
 * carries the short stubs off each nozzle. Legs — the things that get deleted
 * individually — are the gaps between consecutive anchors.
 */
export function anchorsOf(q: Pipe3): Vec3[] {
  const out: Vec3[] = [];
  const a = resolveNode(q.from, q.id);
  if (a) out.push(a.p);
  out.push(...q.pts.map((v) => v3(v.x, v.y, v.z)));
  const b = resolveNode(q.to, q.id);
  if (b) out.push(b.p);
  return out;
}

/** Midpoint of every leg, where its delete marker sits. */
export function legMidpoints(q: Pipe3): { index: number; p: Vec3 }[] {
  const a = anchorsOf(q);
  const out: { index: number; p: Vec3 }[] = [];
  for (let i = 0; i < a.length - 1; i++)
    out.push({
      index: i,
      p: v3((a[i].x + a[i + 1].x) / 2, (a[i].y + a[i + 1].y) / 2, (a[i].z + a[i + 1].z) / 2),
    });
  return out;
}

/**
 * Delete a single leg, splitting the run in two.
 *
 * The piece before the gap keeps the original id — so the selection and
 * anything teed onto it survive — and the piece after it becomes a new run.
 * Either side collapses to nothing if the deleted leg was the first or last,
 * in which case only one piece comes back.
 *
 * @returns the runs that should replace `q`; empty if it disappears entirely.
 */
export function removeLeg(q: Pipe3, index: number): Pipe3[] {
  const anchors = anchorsOf(q);
  if (index < 0 || index >= anchors.length - 1) return [q];
  const share = splitLegNotes(q.legNotes3, anchorParams(anchors), index);
  const carry = { r: q.r, bend: q.bend, service: q.service, color: q.color };
  const asPoint = (v: Vec3) => ({ x: v.x, y: v.y, z: v.z });
  const out: Pipe3[] = [];

  // Everything up to the near side of the gap.
  if (index >= 1) {
    out.push({
      ...carry,
      id: q.id,
      from: q.from,
      pts: q.pts.slice(0, index - 1),
      to: { p: asPoint(anchors[index]) },
      label: q.label,
      legNotes3: share.before,
    });
  }
  // Everything from the far side of the gap onward.
  if (index + 1 <= anchors.length - 2) {
    out.push({
      ...carry,
      id: out.length ? nextPipeId() : q.id,
      from: { p: asPoint(anchors[index + 1]) },
      pts: q.pts.slice(index + 1),
      to: q.to,
      label: q.label,
      legNotes3: share.after,
    });
  }
  return out;
}

/**
 * True when two legs run down the same line, either way along it.
 *
 * Deliberately not "which axis does it mostly follow": a leg running diagonally
 * to a nozzle has a dominant axis like any other, and treating that as parallel
 * would let a length be driven into a run that cannot actually absorb it — the
 * far leg would come out a different length than the arithmetic promised. Only
 * genuinely collinear legs can trade length one-for-one.
 */
function collinear(a: Vec3, b: Vec3): boolean {
  const la = Math.hypot(a.x, a.y, a.z);
  const lb = Math.hypot(b.x, b.y, b.z);
  if (la < 1e-6 || lb < 1e-6) return false;
  const dot = (a.x * b.x + a.y * b.y + a.z * b.z) / (la * lb);
  return Math.abs(dot) > 0.9995;
}

/**
 * Why a leg's length cannot be typed in — or null when it can.
 *
 * Driving a leg means sliding everything downstream of it along that leg's own
 * axis. It is only sound while the run can absorb the change: either the run
 * ends in mid-air and the tail simply travels, or its LAST leg runs along the
 * same axis and can give up exactly what this leg takes. A run pinned at both
 * ends whose final leg crosses the driven one has no slack at all —
 * lengthening it would either skew a leg off-axis or drag a nozzle off its
 * machine. That is refused with a reason rather than fudged, because a drawing
 * that quietly moves equipment to satisfy a typed number is worse than one
 * that says it cannot.
 */
export function whyNotDrivable(q: Pipe3, leg: number): string | null {
  const a = anchorsOf(q);
  // Index arithmetic below assumes anchors are [from, ...pts, to].
  if (a.length !== q.pts.length + 2) return 'an end of this run is missing';
  if (leg < 0 || leg >= a.length - 1) return 'that leg is no longer there';
  if (q.to.p) return null;
  const last = a.length - 2;
  if (leg === last) return 'both ends are pinned — this leg is as long as the gap it spans';
  if (!collinear(vSub(a[leg + 1], a[leg]), vSub(a[last + 1], a[last])))
    return 'the run has no slack along this axis — move the equipment instead';
  return null;
}

/**
 * Set one leg's length, in world units.
 *
 * Everything downstream slides along the leg's own axis. A run that ends in
 * mid-air just travels; one pinned to a nozzle keeps it, and the final leg
 * absorbs the difference.
 *
 * @returns false, leaving the run untouched, if it cannot take the length.
 */
export function driveLegLength(q: Pipe3, leg: number, want: number): boolean {
  if (!(want > 0)) return false;
  if (whyNotDrivable(q, leg)) return false;
  const a = anchorsOf(q);
  const d = vSub(a[leg + 1], a[leg]);
  const len = Math.hypot(d.x, d.y, d.z);
  if (len < 1e-6) return false;
  const u = vNorm(d);
  const delta = want - len;
  const lastIdx = a.length - 1;
  // A free tail travels whole; an anchored one keeps its last point put.
  const stop = q.to.p ? lastIdx : lastIdx - 1;
  if (leg + 1 > stop) return false;
  if (!q.to.p) {
    // The final leg is parallel to this one (whyNotDrivable checked), so its
    // signed extent along u tells us what slack there is. Refuse a length that
    // would consume it or fold the run back through itself.
    const f = vSub(a[lastIdx], a[lastIdx - 1]);
    const s = f.x * u.x + f.y * u.y + f.z * u.z;
    const ns = s - delta;
    if (ns * s <= 0 || Math.abs(ns) <= 1) return false;
  }
  for (let j = leg + 1; j <= stop; j++)
    movePipe3Point(q, j - 1, vAdd(a[j], vMul(u, delta)));
  return true;
}

// ─── Leg notes ────────────────────────────────────────────────────────────
//
// A caption, or a hidden figure, belongs to a LENGTH OF PIPE — not to a slot in
// an array. Everything below exists to keep that true while the run underneath
// is reversed, joined, split or stretched.

/** Cumulative normalised position of every vertex along a polyline, 0..1. */
export function anchorParams(line: Vec3[]): number[] {
  const out = [0];
  if (line.length < 2) return out;
  const seg: number[] = [];
  let total = 0;
  for (let i = 1; i < line.length; i++) {
    const d = Math.hypot(line[i].x - line[i - 1].x, line[i].y - line[i - 1].y, line[i].z - line[i - 1].z);
    seg.push(d);
    total += d;
  }
  let run = 0;
  for (let i = 0; i < seg.length; i++) {
    run += seg[i];
    out.push(total > 1e-9 ? run / total : (i + 1) / seg.length);
  }
  return out;
}

/** Where each leg starts, ends and sits, along the run. */
export function legSpans(line: Vec3[]): { t0: number; t1: number; mid: number }[] {
  const p = anchorParams(line);
  const out: { t0: number; t1: number; mid: number }[] = [];
  for (let i = 0; i + 1 < p.length; i++) out.push({ t0: p[i], t1: p[i + 1], mid: (p[i] + p[i + 1]) / 2 });
  return out;
}

/**
 * The note lying within a stretch of the run, if any.
 *
 * A note belongs to the leg whose span contains it; if two ever share one —
 * which a split or a join can produce — the one nearest the middle wins, so the
 * caption most squarely on this piece of pipe is the one that shows.
 *
 * Taking a SPAN rather than a polyline and an index is what lets a caller work
 * from what the drawing actually measured. The list of drawn legs skips the
 * ones too short to dimension and the ones switched off, so an index into it is
 * not an index into the run — reconstructing a polyline from it would quietly
 * shift every note along by however many legs were missing.
 */
export function noteInSpan(notes: LegNote[] | undefined, t0: number, t1: number): LegNote | undefined {
  if (!notes || !notes.length) return undefined;
  const mid = (t0 + t1) / 2;
  let best: LegNote | undefined;
  let bestD = Infinity;
  for (const n of notes) {
    if (n.t < t0 || n.t > t1) continue;
    const d = Math.abs(n.t - mid);
    if (d < bestD) {
      bestD = d;
      best = n;
    }
  }
  return best;
}

/** Write, change or clear the note on a stretch of the run. */
export function setNoteInSpan(host: LegNoteHost, t0: number, t1: number, patch: Partial<LegNote>): void {
  const mid = (t0 + t1) / 2;
  const was = noteInSpan(host.legNotes3, t0, t1);
  // One leg, one note: anything already inside this span is replaced.
  const kept = (host.legNotes3 ?? []).filter((n) => n.t < t0 || n.t > t1);
  const merged: LegNote = { ...(was ?? {}), ...patch, t: mid };
  if (!merged.text) delete merged.text;
  if (!merged.off) delete merged.off;
  if (merged.text !== undefined || merged.off) kept.push(merged);
  kept.sort((a, b) => a.t - b.t);
  if (kept.length) host.legNotes3 = kept;
  else delete host.legNotes3;
}

/** The note on leg `i` of a polyline. */
export function noteAt(notes: LegNote[] | undefined, line: Vec3[], i: number): LegNote | undefined {
  const s = legSpans(line)[i];
  return s ? noteInSpan(notes, s.t0, s.t1) : undefined;
}

/** A run or connector that can carry leg notes. */
export interface LegNoteHost {
  legNotes3?: LegNote[];
}

/**
 * Write, change or clear the note on leg `i` of a polyline.
 * A note with neither a caption nor a hidden figure is not a note, so it is
 * dropped rather than left behind as an empty record.
 */
export function setNoteAt(host: LegNoteHost, line: Vec3[], i: number, patch: Partial<LegNote>): void {
  const s = legSpans(line)[i];
  if (s) setNoteInSpan(host, s.t0, s.t1, patch);
}

/** True when any leg of this run is marked undimensioned. */
export function hasHiddenLegs(host: LegNoteHost): boolean {
  return !!host.legNotes3?.some((n) => n.off);
}

/** Show every hidden leg figure of a run again, keeping the captions. */
export function showAllLegs(host: LegNoteHost): boolean {
  if (!host.legNotes3?.length) return false;
  let any = false;
  const kept: LegNote[] = [];
  for (const n of host.legNotes3) {
    if (n.off) any = true;
    const { off: _off, ...rest } = n;
    if (rest.text !== undefined) kept.push(rest);
  }
  if (!any) return false;
  if (kept.length) host.legNotes3 = kept;
  else delete host.legNotes3;
  return true;
}

/**
 * Carry a run's leg notes through a reversal or a join.
 * `map` is the very same normalised-position transform that carries tee
 * anchors across — see `retargetAnchors` and its use in `commitRun`.
 */
export function retargetLegNotes(host: LegNoteHost, map: (t: number) => number): void {
  if (!host.legNotes3?.length) return;
  host.legNotes3 = host.legNotes3
    .map((n) => ({ ...n, t: Math.min(1, Math.max(0, map(n.t))) }))
    .sort((a, b) => a.t - b.t);
}

/** Move one run's leg notes onto another, through the same transform. */
export function mergeLegNotes(into: LegNoteHost, from: LegNoteHost, map: (t: number) => number): void {
  if (!from.legNotes3?.length) return;
  const moved = from.legNotes3.map((n) => ({ ...n, t: Math.min(1, Math.max(0, map(n.t))) }));
  const all = [...(into.legNotes3 ?? []), ...moved].sort((a, b) => a.t - b.t);
  if (all.length) into.legNotes3 = all;
}

/**
 * Split a run's notes between the two pieces a deleted leg leaves behind.
 * Each piece renormalises its own share; whatever sat on the leg that went is
 * gone with it.
 */
function splitLegNotes(
  notes: LegNote[] | undefined,
  params: number[],
  index: number,
): { before?: LegNote[]; after?: LegNote[] } {
  if (!notes?.length) return {};
  const tA = params[index];
  const tB = params[index + 1];
  const before: LegNote[] = [];
  const after: LegNote[] = [];
  for (const n of notes) {
    if (n.t <= tA && tA > 1e-9) before.push({ ...n, t: n.t / tA });
    else if (n.t >= tB && tB < 1 - 1e-9) after.push({ ...n, t: (n.t - tB) / (1 - tB) });
  }
  return { before: before.length ? before : undefined, after: after.length ? after : undefined };
}

/** The draggable vertices of a pipe: free ends plus every intermediate bend. */
export function editableVertices(q: Pipe3): { index: number; p: Vec3 }[] {
  const out: { index: number; p: Vec3 }[] = [];
  if (q.from.p) out.push({ index: -1, p: v3(q.from.p.x, q.from.p.y, q.from.p.z) });
  q.pts.forEach((v, i) => out.push({ index: i, p: v3(v.x, v.y, v.z) }));
  if (q.to.p) out.push({ index: q.pts.length, p: v3(q.to.p.x, q.to.p.y, q.to.p.z) });
  return out;
}

/** Human-readable summary for the HUD. */
export function describePipe3(q: Pipe3): string {
  if (q.label) return q.label;
  const ends = [q.from, q.to].map((n) => {
    if (n.shapeId) return S.shapes.find((s) => s.id === n.shapeId)?.label || 'equipment';
    if (n.pipeId) return 'tee';
    return 'open end';
  });
  return `${ends[0]} → ${ends[1]}`;
}

/** Shapes are needed by `resolveNode`; re-exported so callers need one import. */
export type { Shape };
